import React from 'react';
import { T } from '../constants';
import { fmtDateTime, mlColor, dirStyle } from '../helpers';
import SystemBadge from './badges/SystemBadge';
import GradeBadge from './badges/GradeBadge';
import ClaudeVerdictBadge from './badges/ClaudeVerdictBadge';

// All decisions within this window of the newest are treated as one concurrent batch
const CLUSTER_WINDOW_SEC = 60;

function actionRank(a) {
  if (a === 'TRADE')   return 0;
  if (a === 'OBSERVE') return 1;
  return 2;
}

export default function ActiveSignals({ decisions, selected, onSelect }) {
  if (!decisions.length) return null;

  const newest  = new Date(decisions[0].signal_ts);
  const cluster = decisions
    .filter(d => (newest - new Date(d.signal_ts)) / 1000 <= CLUSTER_WINDOW_SEC)
    .sort((a, b) => actionRank(a.action_label || a.action) - actionRank(b.action_label || b.action));

  const tradeCount   = cluster.filter(d => (d.action_label || d.action) === 'TRADE').length;
  const observeCount = cluster.filter(d => (d.action_label || d.action) === 'OBSERVE').length;
  const hasTrade     = tradeCount > 0;
  const hasObserve   = observeCount > 0;

  const headerColor = hasTrade ? T.bull : hasObserve ? T.purple : T.textDim;
  const headerLabel = hasTrade
    ? `${tradeCount} Active Trade${tradeCount > 1 ? 's' : ''}`
    : hasObserve
    ? `${observeCount} Observe Signal${observeCount > 1 ? 's' : ''}`
    : 'Signal Cluster';

  return (
    <div className="active-signals">
      <div className="active-signals-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {(hasTrade || hasObserve) && (
            <span className="pulse-dot" style={{ background: headerColor }} />
          )}
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: headerColor,
          }}>
            {headerLabel}
          </span>
          <span style={{ fontSize: 10, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
            {fmtDateTime(decisions[0].signal_ts)}
          </span>
        </div>
        <span style={{ fontSize: 10, color: T.textMuted }}>
          {cluster.length} decision{cluster.length !== 1 ? 's' : ''} · last 60s
        </span>
      </div>

      <div className="active-signals-grid">
        {cluster.map((d, i) => {
          const isTrade   = (d.action_label || d.action) === 'TRADE';
          const isObserve = (d.action_label || d.action) === 'OBSERVE';
          const isBlock   = (d.action_label || d.action || '').startsWith('BLOCK');
          const accent    = isTrade ? T.bull : isObserve ? T.purple : T.bear;
          const accentBg  = isTrade ? T.bullBg : isObserve ? T.purpleBg : 'rgba(79,89,102,0.06)';
          const accentBdr = isTrade ? T.bullBorder : isObserve ? T.purpleBorder : 'rgba(79,89,102,0.25)';
          const isSelected = selected &&
            selected.signal_ts === d.signal_ts &&
            selected.system_id === d.system_id;

          return (
            <div
              key={`${d.system_id}-${d.signal_ts}-${i}`}
              className="active-card"
              onClick={() => onSelect(d)}
              style={{
                borderColor: isSelected ? accent : accentBdr,
                borderLeftColor: accent,
                background: isSelected ? accentBg : undefined,
                opacity: isBlock ? 0.6 : 1,
                boxShadow: isSelected && !isBlock ? `0 0 18px ${accent}25` : undefined,
              }}
            >
              <div className="active-card-top">
                <SystemBadge systemId={d.system_id} />
                {d.direction && (
                  <span className="badge" style={{ ...dirStyle(d.direction), fontSize: 9, padding: '1px 5px' }}>
                    {d.direction}
                  </span>
                )}
              </div>

              <div className="active-card-action" style={{
                color: accent,
                textShadow: !isBlock ? `0 0 20px ${accent}45` : 'none',
              }}>
                {d.action_label || d.action}
              </div>

              <div className="active-card-meta">
                <GradeBadge grade={d.grade} decision={d} />
                <ClaudeVerdictBadge verdict={d.claude_verdict} score={d.claude_score} reasoning={d.claude_reasoning} />
                <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: mlColor(d.ml_score) }}>
                  {Math.round(parseFloat(d.ml_score) || 0)}
                </span>
              </div>

              {isTrade && (
                <div style={{ fontSize: 10, fontFamily: 'Roboto Mono, monospace', marginTop: 6 }}>
                  <span style={{ color: T.bull }}>{d.contracts ?? '—'}c</span>
                  {d.entry_price && <span style={{ color: T.textDim }}> · entry ${parseFloat(d.entry_price).toLocaleString()}</span>}
                  {(d.stop_price || d.stop_pts) && (
                    <span style={{ color: T.bear }}>
                      {d.stop_price
                        ? ` · SL $${parseFloat(d.stop_price).toLocaleString()}`
                        : ` · SL ${d.stop_pts}pt`}
                    </span>
                  )}
                  {d.target_price && (
                    <span style={{ color: T.bull }}>{` · TP $${parseFloat(d.target_price).toLocaleString()}`}</span>
                  )}
                </div>
              )}
              {isBlock && d.gate_blocked && (
                <div style={{
                  fontSize: 10, color: T.textMuted, fontFamily: 'Roboto Mono, monospace',
                  marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  Gate: {d.gate_blocked}
                </div>
              )}
              {isObserve && (
                <div style={{ fontSize: 10, color: T.purple, fontFamily: 'Roboto Mono, monospace', marginTop: 6 }}>
                  {d.trigger ? `Trigger: ${d.trigger}` : 'Monitoring…'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
